import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../api/axiosInstance";
import { SearchOfIndicators } from "../../../interfaces/Rights";
import { Pagination } from "../../../interfaces/Pagination";
import { OpenDataIndicator } from "../../../interfaces/OpenData";
import { flattenRightsData } from "../../../utils/flattenRights";
const { REACT_APP_API_PATH } = process.env;

interface GetSearchOfIndicators {
    search: SearchOfIndicators[] | null
    error?: string | undefined
}

export interface GetIndicatorsQuery {
    indicatorId?: string;
    typeIndicator?: string[];
    derechos?: string[];
    dimensiones?: string[];
    gruposPrioritarios?: string[];
    ods?: string[];
    variables?: string[];
    page?: number
    limit?: number;
    downloadAll?: boolean;
}

interface GetSearchOfIndicatorsQuery {
    search: string;
}

interface GetIndicatorResponse {
    indicators: OpenDataIndicator[]
    error?: boolean
    pagination: Pagination
}

interface ErrorResponse {
    message: string;
}

export const getSearchOfIndicators = createAsyncThunk<GetSearchOfIndicators, GetSearchOfIndicatorsQuery, { rejectValue: ErrorResponse }>(
    "open_data/searchIndicators",
    async ({ search }: GetSearchOfIndicatorsQuery) => {
        try {
            const params = new URLSearchParams();
            if (search) params.append('search', search);

            const response = await axiosInstance.get(`${REACT_APP_API_PATH}/datos-abiertos?${params.toString()}`);
            const data = response.data.data
            return {
                search: data.data
            }
        } catch (error: any) {
            return {
                search: null,
                error: "No se encontraron indicadores."
            };
        }
    }
);

export const getIndicators = createAsyncThunk<GetIndicatorResponse, GetIndicatorsQuery, { rejectValue: ErrorResponse }>(
    "open_data/getIndicators",
    async ({ indicatorId, typeIndicator, page, limit, dimensiones, derechos, gruposPrioritarios, ods, variables }: GetIndicatorsQuery) => {
        try {
            const params = new URLSearchParams();

            if (indicatorId !== undefined) params.append('indicatorId', indicatorId.toString());
            if (page) params.append('page', page.toString());
            if (limit) params.append('limit', limit.toString());

            if (typeIndicator && typeIndicator.length > 0) {
                typeIndicator.forEach(type => params.append('indicatorType', type));
            }

            if (dimensiones && dimensiones.length > 0) {
                dimensiones.forEach(dimension => params.append('dimensiones', dimension));
            }

            if (derechos && derechos.length > 0) {
                derechos.forEach(derecho => params.append('derechos', derecho));
            }

            if (gruposPrioritarios && gruposPrioritarios.length > 0) {
                gruposPrioritarios.forEach(grupo => params.append('gruposPrioritarios', grupo));
            }

            if (ods && ods.length > 0) {
                ods.forEach(ods => params.append('ods', ods));
            }

            if (variables && variables.length > 0) {
                variables.forEach(variables => params.append('variables', variables));
            }

            const response = await axiosInstance.get(`${REACT_APP_API_PATH}/datos-abiertos?${params.toString()}`);
            const data = response.data.data;

            return {
                indicators: data.data,
                pagination: {
                    currentPage: Number(data.page),
                    pageSize: Number(data.limit),
                    totalItems: Number(data.totalItems),
                    totalPages: Number(data.totalPages),
                },
                error: false
            }
        } catch (error: any) {
            return {
                indicators: [],
                pagination: { totalItems: 0, currentPage: 0, totalPages: 0, pageSize: 0 },
                count: 0,
                error: true,
            };
        }
    }
);

export const getFilterOptions = createAsyncThunk(
    'open_data/getFilterOptions',
    async () => {
        try {
            const response = await axiosInstance.get(`${REACT_APP_API_PATH}/datos-abiertos/filter-options`);
            const data = response.data.data;
            const flattenedData = flattenRightsData(data.derechos);

            return {
                derechos: flattenedData.derechos,
                tiposIndicadores: flattenedData.tiposIndicadores,
                dimensiones: flattenedData.dimensiones,
                ods: data.ods,
                priorityGroups: data.priority_groups,
                variables: data.variables
            }
        } catch (error) {
            return {
                derechos: [],
                tiposIndicadores: [],
                dimensiones: [],
                ods: [],
                priorityGroups: [],
                variables: []
            }
        }
    }
);

export const getDatasetCount = createAsyncThunk(
    "open_data/getDatasetCount",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axiosInstance.get(
                `${REACT_APP_API_PATH}/datos-abiertos/count-data-set`
            );

            return response.data.data.count;
        } catch (err: any) {
            return rejectWithValue(err.response?.data || "Error al obtener el count");
        }
    }
);

export const getAllIndicatorsForDownload = createAsyncThunk<OpenDataIndicator[], GetIndicatorsQuery, { rejectValue: ErrorResponse }>(
    "open_data/getAllIndicatorsForDownload",
    async ({ typeIndicator, dimensiones, derechos, gruposPrioritarios, ods, variables }: GetIndicatorsQuery) => {
        try {
            const params = new URLSearchParams();

            params.append('downloadAll', 'true');

            if (typeIndicator && typeIndicator.length > 0) {
                typeIndicator.forEach(type => params.append('indicatorType', type));
            }

            if (dimensiones && dimensiones.length > 0) {
                dimensiones.forEach(dimension => params.append('dimensiones', dimension));
            }

            if (derechos && derechos.length > 0) {
                derechos.forEach(derecho => params.append('derechos', derecho));
            }

            if (gruposPrioritarios && gruposPrioritarios.length > 0) {
                gruposPrioritarios.forEach(grupo => params.append('gruposPrioritarios', grupo));
            }

            if (ods && ods.length > 0) {
                ods.forEach(ods => params.append('ods', ods));
            }

            if (variables && variables.length > 0) {
                variables.forEach(variables => params.append('variables', variables));
            }

            const response = await axiosInstance.get(`${REACT_APP_API_PATH}/datos-abiertos?${params.toString()}`);
            const data = response.data.data;

            return data.data
        } catch (error: any) {
            return []
        }
    }
);